import type { FunctionComponent, SVGProps } from 'react';
import { useEffect, useState, memo, useMemo } from 'react';

import { usePromiseValue } from '../../hooks/use-promise-value';
import { createClassName, createStyleHelper } from '../../utils/class-names';

import styles from './icon.module.scss';

const style = createStyleHelper(styles, 'icon');

export interface IIconProps {
    className?: string;
    name: string;
    size?: IconSize | number;
    color?: string;
    inline?: boolean;
    onClick?: () => void;
}

export enum IconSize {
    X_SMALL = 'x-small',
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    AUTO = 'auto',
}

const ICON_CACHE: Record<
    string,
    Promise<{
        default: string;
        ReactComponent: FunctionComponent<SVGProps<SVGSVGElement> & { title?: string }>;
    }>
> = {};

export const Icon: FunctionComponent<IIconProps> = memo(
    ({
        className,
        name,
        size = IconSize.X_SMALL,
        color = 'currentcolor',
        inline = false,
        onClick,
    }) => {
        const [hasError, setHasError] = useState(false);
        const { data, resolving } = usePromiseValue<{
            default: string;
            ReactComponent: FunctionComponent<SVGProps<SVGSVGElement> & { title?: string }>;
        }>(
            useMemo(
                () =>
                    ICON_CACHE[name] ??
                    (ICON_CACHE[name] = import(`./assets/${name.toLowerCase()}.svg?raw`)),
                [name],
            ),
        );

        useEffect(() => {
            setHasError(false);
        }, [name]);

        if (resolving || !data) {
            return (
                <div className={style(undefined, { loading: true, [size]: true, inline })}></div>
            );
        }

        const SvgIcon = data.ReactComponent;

        return (
            <div
                className={createClassName([className, style(undefined, { [size]: true, inline })])}
                onClick={onClick}
            >
                {!hasError ? (
                    <SvgIcon
                        className={style('image')}
                        fill={color}
                        onError={() => setHasError(true)}
                        name={name}
                    />
                ) : null}
            </div>
        );
    },
);
